import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { bindRealtimeVisibilityPause } from "@/lib/realtime-visibility-pause";
import { REALTIME_PAUSE_ON_HIDDEN_ENABLED } from "@/lib/workspace-conversation-realtime-hub";
import { useToast } from "@/hooks/use-toast";
import type { RealtimeChannel } from "@supabase/supabase-js";

export interface GroupStage {
  id: string;
  group_id: string;
  name: string;
  slug: string;
  color: string;
  order_index: number;
  created_at?: string;
  updated_at?: string;
}

export function useGroupStages(groupId: string | null) {
  const [stages, setStages] = useState<GroupStage[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchStages = useCallback(async () => {
    if (!groupId) {
      setStages([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("group_stages")
        .select("*")
        .eq("group_id", groupId)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setStages(data || []);
    } catch (error: any) {
      console.error("Error fetching group stages:", error);
      toast({
        title: "Erro ao carregar estágios",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [groupId, toast]);

  useEffect(() => {
    fetchStages();
  }, [fetchStages]);

  // Realtime subscription
  useEffect(() => {
    if (!groupId) return;

    const channelName = `group-stages-${groupId}`;
    let channel: RealtimeChannel | null = null;
    const reconcileTimerRef = { current: null as ReturnType<typeof setTimeout> | null };

    const subscribeChannel = () => {
      if (REALTIME_PAUSE_ON_HIDDEN_ENABLED && typeof document !== "undefined" && document.hidden) {
        return;
      }
      if (channel) return;
      channel = subscribePostgresChannel(
        channelName,
        {
          event: "*",
          schema: "public",
          table: "group_stages",
          filter: `group_id=eq.${groupId}`,
        },
        () => {
          fetchStages();
        },
      );
    };

    const teardownChannel = () => {
      if (!channel) return;
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
      channel = null;
    };

    const reconcileStages = () => {
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
      reconcileTimerRef.current = setTimeout(() => {
        fetchStages();
      }, 300);
    };

    const unbind = bindRealtimeVisibilityPause({
      pauseEnabled: REALTIME_PAUSE_ON_HIDDEN_ENABLED,
      subscribe: subscribeChannel,
      teardown: teardownChannel,
      onReconcile: reconcileStages,
    });

    return () => {
      unbind();
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    };
  }, [groupId, fetchStages]);

  const createStage = async (data: { name: string; color: string }) => {
    if (!groupId) return null;

    try {
      const slug = data.name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

      const maxOrder = stages.reduce((max, s) => Math.max(max, s.order_index), -1);

      const { data: newStage, error } = await supabase
        .from("group_stages")
        .insert({
          group_id: groupId,
          name: data.name,
          slug,
          color: data.color,
          order_index: maxOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Estágio criado",
        description: `O estágio "${data.name}" foi criado com sucesso.`,
      });

      return newStage;
    } catch (error: any) {
      toast({
        title: "Erro ao criar estágio",
        description: error.message,
        variant: "destructive",
      });
      return null;
    }
  };

  const updateStage = async (id: string, data: Partial<GroupStage>) => {
    try {
      const { error } = await supabase
        .from("group_stages")
        .update(data)
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Estágio atualizado",
        description: "As alterações foram salvas.",
      });

      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar estágio",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteStage = async (id: string) => {
    try {
      const { error, count } = await supabase
        .from("group_stages")
        .delete({ count: "exact" })
        .eq("id", id);

      if (error) throw error;

      if (!count) {
        toast({
          title: "Não foi possível excluir",
          description: "Você não tem permissão para remover esta coluna.",
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: "Estágio excluído",
        description: "O estágio foi removido com sucesso.",
      });

      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao excluir estágio",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const reorderStages = async (orderedIds: string[]) => {
    try {
      const updates = orderedIds.map((id, index) => ({
        id,
        order_index: index,
      }));

      for (const update of updates) {
        await supabase
          .from("group_stages")
          .update({ order_index: update.order_index })
          .eq("id", update.id);
      }

      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao reordenar estágios",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    stages,
    loading,
    createStage,
    updateStage,
    deleteStage,
    reorderStages,
    refetch: fetchStages,
  };
}
